/**
 * adapters/indexed-db-storage.ts
 *
 * Saved games in IndexedDB, written so a save on a big board stays small:
 * the game's own record holds the board and notes as typed arrays (a
 * 200×200 board is 40 KB, not 40,000 boxed numbers) and how many moves it
 * has; the move log and the undo stack are kept apart in chunks of
 * `MOVE_CHUNK` moves. The log only grows, so a save rewrites just the
 * chunks whose moves changed since this storage last saved or loaded the
 * game (compared by identity: the history shares unchanged moves). Each
 * save bumps the game's revision, so a save by another tab in between is
 * noticed and everything is written. Games saved whole, before chunks, still
 * load.
 */
import type { IDBPTransaction } from 'idb';
import type { MoveCommand } from '../engine/history';
import type { NoteArrow } from '../engine/note-arrows';
import type { PersistedSession, SavedSessionInfo, StoragePort } from '../ports/storage';
import { lazyClientDb, SESSION_MOVES_STORE, SESSION_STORE } from './indexed-db';

/** Moves per stored chunk. */
export const MOVE_CHUNK = 500;

type MovePart = 'log' | 'past';

/** A game's own record, as stored. */
interface StoredSession {
  puzzleId: string;
  board: Int8Array;
  notes?: Int32Array;
  noteArrows?: NoteArrow[];
  elapsedMs: number;
  visualClueCompletion?: PersistedSession['visualClueCompletion'];
  savedAt: string;
  /** How many moves each part has (the moves are chunks in SESSION_MOVES_STORE). */
  moves: Record<MovePart, number>;
  /** Bumped by every save. */
  rev: number;
}

/** The moves of the game this storage last saved or loaded, at the revision they were stored as. */
interface KnownMoves extends Record<MovePart, readonly MoveCommand[]> {
  puzzleId: string;
  rev: number;
}

type Tx = IDBPTransaction<unknown, string[], 'readwrite'>;

const PARTS: MovePart[] = ['log', 'past'];

/** A part's chunks from `from` on. */
function partRange(puzzleId: string, part: MovePart, from = 0): IDBKeyRange {
  return IDBKeyRange.bound([puzzleId, part, from], [puzzleId, part, Infinity]);
}

function gameRange(puzzleId: string): IDBKeyRange {
  return IDBKeyRange.bound([puzzleId], [puzzleId, []]);
}

/** A game saved whole, before chunks. */
function isWhole(record: StoredSession | PersistedSession): record is PersistedSession {
  return Array.isArray((record as PersistedSession).pastMoves);
}

function partOf(session: PersistedSession, part: MovePart): readonly MoveCommand[] {
  return part === 'log' ? session.log ?? session.pastMoves : session.pastMoves;
}

export class IndexedDBStorage implements StoragePort {
  private readonly getDb = lazyClientDb();
  /** What the next save of that game compares against (one game: a storage serves one game at a time). */
  private known: KnownMoves | null = null;

  async loadSession(puzzleId: string): Promise<PersistedSession | null> {
    const db = await this.getDb();
    const tx = db.transaction([SESSION_STORE, SESSION_MOVES_STORE], 'readonly');
    const record = (await tx.objectStore(SESSION_STORE).get(puzzleId)) as StoredSession | PersistedSession | undefined;
    if (!record) return null;
    if (isWhole(record)) {
      this.known = null;
      return record;
    }
    const moves = tx.objectStore(SESSION_MOVES_STORE);
    const [log, pastMoves] = await Promise.all(PARTS.map(async (part) =>
      ((await moves.getAll(partRange(puzzleId, part))) as MoveCommand[][]).flat().slice(0, record.moves[part])));
    // The game keeps these move objects, which its next save compares by
    // identity; the arrays are copied, as the game may add to its own.
    // A game this storage saved and still knows keeps what it knows: those
    // are the objects the game has.
    if (this.known?.puzzleId !== puzzleId || this.known.rev !== record.rev) {
      this.known = { puzzleId, rev: record.rev, log: [...log], past: [...pastMoves] };
    }
    return {
      puzzleId,
      board: Array.from(record.board),
      pastMoves,
      log,
      elapsedMs: record.elapsedMs,
      ...(record.visualClueCompletion ? { visualClueCompletion: record.visualClueCompletion } : {}),
      ...(record.notes ? { notes: Array.from(record.notes) } : {}),
      ...(record.noteArrows ? { noteArrows: record.noteArrows } : {}),
      savedAt: record.savedAt,
    };
  }

  async saveSession(session: PersistedSession): Promise<void> {
    const db = await this.getDb();
    const tx = db.transaction([SESSION_STORE, SESSION_MOVES_STORE], 'readwrite') as unknown as Tx;
    const stored = (await tx.objectStore(SESSION_STORE).get(session.puzzleId)) as StoredSession | PersistedSession | undefined;
    const storedRev = stored && !isWhole(stored) ? stored.rev : undefined;
    // What's in the database now, unless it was saved whole or by another tab since.
    const known = this.known?.puzzleId === session.puzzleId && this.known.rev === storedRev ? this.known : undefined;
    // Every write is sent at once and the transaction awaited once: each
    // await is a trip through the event loop, which a busy page makes slow.
    const writes: Array<Promise<unknown>> = [];
    for (const part of PARTS) this.writePart(tx, writes, session.puzzleId, part, partOf(session, part), known?.[part]);
    const rev = (storedRev ?? 0) + 1;
    const record: StoredSession = {
      puzzleId: session.puzzleId,
      board: Int8Array.from(session.board),
      ...(session.notes ? { notes: Int32Array.from(session.notes) } : {}),
      ...(session.noteArrows ? { noteArrows: session.noteArrows } : {}),
      elapsedMs: session.elapsedMs,
      ...(session.visualClueCompletion ? { visualClueCompletion: session.visualClueCompletion } : {}),
      savedAt: session.savedAt,
      moves: { log: partOf(session, 'log').length, past: session.pastMoves.length },
      rev,
    };
    writes.push(tx.objectStore(SESSION_STORE).put(record));
    await Promise.all([...writes, tx.done]);
    this.known = { puzzleId: session.puzzleId, rev, log: [...partOf(session, 'log')], past: [...session.pastMoves] };
  }

  /**
   * Writes the chunks of `moves` that differ from `known` (all of them when
   * nothing is known), and deletes chunks past the end (the undo stack shrinks).
   */
  private writePart(tx: Tx, writes: Array<Promise<unknown>>, puzzleId: string, part: MovePart, moves: readonly MoveCommand[], known: readonly MoveCommand[] | undefined): void {
    const store = tx.objectStore(SESSION_MOVES_STORE);
    const chunks = Math.ceil(moves.length / MOVE_CHUNK);
    for (let chunk = 0; chunk < chunks; chunk++) {
      const start = chunk * MOVE_CHUNK;
      const end = Math.min(moves.length, start + MOVE_CHUNK);
      if (known && sameMoves(moves, known, start, end)) continue;
      writes.push(store.put(moves.slice(start, end), [puzzleId, part, chunk]));
    }
    writes.push(store.delete(partRange(puzzleId, part, chunks)));
  }

  async deleteSession(puzzleId: string): Promise<void> {
    const db = await this.getDb();
    const tx = db.transaction([SESSION_STORE, SESSION_MOVES_STORE], 'readwrite');
    await Promise.all([
      tx.objectStore(SESSION_STORE).delete(puzzleId),
      tx.objectStore(SESSION_MOVES_STORE).delete(gameRange(puzzleId)),
      tx.done,
    ]);
    if (this.known?.puzzleId === puzzleId) this.known = null;
  }

  async listSessions(): Promise<SavedSessionInfo[]> {
    const db = await this.getDb();
    const records = (await db.getAll(SESSION_STORE)) as Array<StoredSession | PersistedSession>;
    return records.map(({ puzzleId, savedAt }) => ({ puzzleId, savedAt }));
  }

  async clearSessions(): Promise<void> {
    const db = await this.getDb();
    const tx = db.transaction([SESSION_STORE, SESSION_MOVES_STORE], 'readwrite');
    await Promise.all([tx.objectStore(SESSION_STORE).clear(), tx.objectStore(SESSION_MOVES_STORE).clear(), tx.done]);
    this.known = null;
  }
}

/** Whether `moves` and `known` hold the same move objects from `start` to `end`. */
function sameMoves(moves: readonly MoveCommand[], known: readonly MoveCommand[], start: number, end: number): boolean {
  if (known.length < end) return false;
  // A chunk that was partly full last time and is longer now has changed.
  if (Math.min(known.length, start + MOVE_CHUNK) !== end) return false;
  for (let i = start; i < end; i++) if (moves[i] !== known[i]) return false;
  return true;
}
